import { useCachedEarlyAccessPluginsOrgEnrolledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCachedEarlyAccessPluginsOrgEnrolledVariation.js';
import { useEarlyAccessPluginsOrgEnrolledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useEarlyAccessPluginsOrgEnrolledVariation.js';
import { useEffectivePermissions } from '#pplx/frontend/apps/ask/organization/hooks/useEffectivePermissions.js';
import { useSessionOrgUUID } from '#pplx/frontend/apps/ask/organization/hooks/useSessionOrgUUID.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { isEarlyAccess } from '#pplx/frontend/apps/ask/rbac/utilities/earlyAccess.js';
const EARLY_ACCESS_FEATURES = {
    plugins: {
        permissionId: 'early_access_plugins',
        endsOn: '2027-01-19',
        useEnrolled: useEarlyAccessPluginsOrgEnrolledVariation,
        useCachedEnrolled: useCachedEarlyAccessPluginsOrgEnrolledVariation,
    },
};
const WHITE_GLOVE_ORG_SERVICE_TYPE = 'WHITE_GLOVE';
/**
 * Early access for one feature, from both the org's and this member's point of view. Once the end
 * date has passed every member is `allowed` and nothing is read. Before it, early access covers
 * white-glove orgs only: a session without an organization, or in a self-serve or SBKK org, is
 * `allowed` and reads nothing further. A member of a white-glove org that is not enrolled is
 * `allowed`, keeping the feature's existing rollout; in an enrolled org the member must hold the
 * feature's `early_access_<feature>` permission. Until the org, the assignment or the permission set has loaded the answer is
 * `loading`.
 */
export function useEarlyAccess(feature, { reason, skip = false, pinned = false }) {
    const { permissionId, endsOn, useEnrolled, useCachedEnrolled } = EARLY_ACCESS_FEATURES[feature];
    const ended = !isEarlyAccess(endsOn);
    const { uuid: orgUuid } = useSessionOrgUUID();
    const member = !skip && !ended && orgUuid !== undefined;
    const org = useUserOrgData({ reason, enabled: member, shouldRefetchOnMount: false });
    const orgLoading = member && org.isLoading;
    const whiteGlove = member && !orgLoading && org.organization?.org_service_type === WHITE_GLOVE_ORG_SERVICE_TYPE;
    const live = useEnrolled({ defaultValue: false, skip: !whiteGlove || pinned });
    const cached = useCachedEnrolled({ defaultValue: false, skip: !whiteGlove || !pinned });
    const enrolled = whiteGlove && (pinned ? cached : live.variation);
    const status = earlyAccessStatus({
        ended,
        loading: orgLoading || (whiteGlove && !pinned && live.loading),
        enrolled,
    });
    const granted = useEffectivePermissions({ reason, enabled: status === 'active' });
    return {
        status,
        endsOn,
        access: memberAccess({ status, granted, permissionId }),
    };
}
function earlyAccessStatus({ ended, loading, enrolled, }) {
    if (ended)
        return 'ended';
    if (loading)
        return 'loading';
    return enrolled ? 'active' : 'off';
}
function memberAccess({ status, granted, permissionId, }) {
    if (status === 'loading')
        return 'loading';
    if (status !== 'active')
        return 'allowed';
    if (granted.isLoading)
        return 'loading';
    return granted.isLoaded && granted.permissions.has(permissionId) ? 'allowed' : 'denied';
}
//# sourceMappingURL=useEarlyAccess.js.map